/* eslint-disable no-nested-ternary */
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { fDate } from 'src/utils/format-time';
import { fCurrency, fCurrencyRupees } from 'src/utils/format-number';

import { useAppSelector, useAppDispatcher } from 'src/redux/store';
import {
  updateChequeStatus,
  getChequePayments,
  getChequePaymentTotal,
  createChequePayment,
  createSuggestion,
  getChequeReceivedViaOptions,
} from 'src/redux/slices/cheque';

import Label from 'src/components/label';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFCreatableAutocomplete,
} from 'src/components/hook-form';

import { ChequeStatus, IChequeItem, IChequePayment } from 'src/types/cheque';

import DirectToSupplierCheckbox from './direct-to-supplier-checkbox';

interface IUpdateChequeStatus {
  currentCheque: IChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
}

function getNextAvailableStatuses(currentStatus: ChequeStatus, hasSupplier = true): ChequeStatus[] {
  switch (currentStatus) {
    // Banking: deposited into our own account instead of paying a supplier.
    case ChequeStatus.IN_HAND:
      return [ChequeStatus.PAID, ChequeStatus.HOLDING, ChequeStatus.BANKING];
    // Issuer asked us not to bank it: payments move it on (via Recovered, which
    // records a payment), or it goes back to In Hand if they change their mind.
    case ChequeStatus.HOLDING:
      return [ChequeStatus.RECOVERED, ChequeStatus.IN_HAND];
    case ChequeStatus.PAID:
      return [ChequeStatus.RETURN];
    // Stays in Banking for good unless the bank returns it.
    case ChequeStatus.BANKING:
      return [ChequeStatus.RETURN];
    // Recovery doesn't have to wait for the supplier to be settled in full —
    // a returned cheque can be handed over to the sales rep straight away.
    case ChequeStatus.RETURN:
      // A cheque we banked ourselves has no supplier to settle with.
      return hasSupplier ? [ChequeStatus.SETTLED, ChequeStatus.HANDOVER] : [ChequeStatus.HANDOVER];
    case ChequeStatus.SETTLED:
      return [ChequeStatus.HANDOVER];
    case ChequeStatus.HANDOVER:
      return [ChequeStatus.RECOVERED];
    case ChequeStatus.RECOVERING:
      return [ChequeStatus.RECOVERED];
    default:
      return [];
  }
}

function UpdateChequeStatus({ currentCheque, isOpen, onClose }: IUpdateChequeStatus) {
  const dispatch = useAppDispatcher();

  const navigate = useNavigate();

  const receivedViaOptions = useAppSelector((state) => state.cheque.receivedViaOptions.data);

  useEffect(() => {
    if (isOpen) {
      dispatch(getChequeReceivedViaOptions());
    }
  }, [isOpen, dispatch]);

  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState<IChequePayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const updateChequeStatusSchema = useMemo(() => {
    const remaining = currentCheque.amountInCents - paymentTotal;

    return Yup.object().shape({
      status: Yup.string().required('Status is required'),
      holder: Yup.string().when('status', ([status], schema) =>
        status === ChequeStatus.PAID || status === ChequeStatus.HANDOVER
          ? schema.required('Holder is required')
          : schema.optional()
      ),
      date: Yup.date().required('Date is required'),
      paymentAmount: Yup.number().when(['status'], ([status], schema) => {
        if (
          status === ChequeStatus.RECOVERED &&
          (currentCheque.status === ChequeStatus.HOLDING ||
            currentCheque.status === ChequeStatus.HANDOVER ||
            currentCheque.status === ChequeStatus.RECOVERING)
        ) {
          return schema
            .required('Payment amount is required')
            .min(1, 'Payment amount must be at least 1 cent')
            .max(
              remaining,
              `Payment cannot exceed remaining amount (${fCurrency(remaining / 100)})`
            );
        }
        return schema.optional();
      }),
      paymentNote: Yup.string().optional(),
      paidDirectToSupplier: Yup.boolean().default(false),
    });
  }, [currentCheque, paymentTotal]);

  const defaultValues = useMemo(() => {
    const remaining = currentCheque.amountInCents - paymentTotal;

    return {
      status: currentCheque.status,
      date: new Date(),
      holder: currentCheque.status === ChequeStatus.SETTLED ? currentCheque.receivedVia : '',
      paymentAmount: remaining / 100,
      paymentNote: '',
      paidDirectToSupplier: false,
    };
  }, [currentCheque, paymentTotal]);

  const methods = useForm({
    resolver: yupResolver(updateChequeStatusSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const watchStatus = useWatch({
    control,
    name: 'status',
  });

  useEffect(() => {
    const fetchPaymentData = async () => {
      if (
        isOpen &&
        (currentCheque.status === ChequeStatus.HOLDING ||
          currentCheque.status === ChequeStatus.HANDOVER ||
          currentCheque.status === ChequeStatus.RECOVERING)
      ) {
        setLoadingPayments(true);
        try {
          const [totalRes, historyRes] = await Promise.all([
            dispatch(getChequePaymentTotal({ chequeId: currentCheque._id })).unwrap(),
            dispatch(getChequePayments({ chequeId: currentCheque._id })).unwrap(),
          ]);
          setPaymentTotal(totalRes.totalPaid);
          setPaymentHistory(historyRes);
        } catch (error) {
          console.error('Failed to fetch payment data:', error);
          enqueueSnackbar('Could not load payment history', { variant: 'warning' });
        } finally {
          setLoadingPayments(false);
        }
      }
    };

    fetchPaymentData();
  }, [isOpen, currentCheque._id, currentCheque.status, dispatch]);

  // eslint-disable-next-line consistent-return
  const onSubmit = handleSubmit(async (data) => {
    try {
      const isRecoveryWithPayment =
        data.status === ChequeStatus.RECOVERED &&
        (currentCheque.status === ChequeStatus.HOLDING ||
          currentCheque.status === ChequeStatus.HANDOVER ||
          currentCheque.status === ChequeStatus.RECOVERING);

      if (isRecoveryWithPayment) {
        // Create payment - server auto-handles recovery
        const paymentResponse = await dispatch(
          createChequePayment({
            chequeId: currentCheque._id,
            payment: {
              paymentDate: new Date(data.date),
              amountInCents: (data.paymentAmount || 0) * 100,
              note: data.paymentNote || undefined,
              paidDirectToSupplier: data.paidDirectToSupplier || undefined,
            },
          })
        ).unwrap();

        // Check if payment triggered auto-recovery (has recoveryHistory field)
        if (paymentResponse.recoveryHistory) {
          // Full payment - navigate to Recovered tab
          enqueueSnackbar('Payment recorded and cheque recovered successfully');
          navigate(paths.dashboard.cheque.root(ChequeStatus.RECOVERED));
        } else {
          // Partial payment - navigate to Recovering tab
          enqueueSnackbar('Partial payment recorded successfully');
          navigate(paths.dashboard.cheque.root(ChequeStatus.RECOVERING));
        }
      } else {
        // Normal status update (existing logic)
        await dispatch(
          updateChequeStatus({
            chequeID: currentCheque._id,
            updatingCheque: {
              date: new Date(data.date),
              status: data.status as ChequeStatus,
              holder: data.holder,
            },
          })
        ).unwrap();

        enqueueSnackbar('Cheque status changed successfully');
        navigate(paths.dashboard.cheque.root(data.status));
      }

      reset();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message || 'Failed to update cheque', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Change Status</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Are you sure want to change the status of the cheque?
          </Typography>

          <Stack spacing={2}>
            <RHFSelect fullWidth name="status" label="Status" required>
              {getNextAvailableStatuses(currentCheque.status, !currentCheque.bankedAt).map(
                (type, index) => (
                  <MenuItem
                    key={type}
                    value={type}
                    sx={{
                      textTransform: 'capitalize',
                    }}
                  >
                    <Label
                      variant="soft"
                      color={
                        (type === ChequeStatus.PAID && 'success') ||
                        (type === ChequeStatus.RETURN && 'error') ||
                        (type === ChequeStatus.SETTLED && 'warning') ||
                        (type === ChequeStatus.RECOVERING && 'warning') ||
                        (type === ChequeStatus.RECOVERED && 'success') ||
                        (type === ChequeStatus.HANDOVER && 'info') ||
                        (type === ChequeStatus.HOLDING && 'warning') ||
                        (type === ChequeStatus.BANKING && 'info') ||
                        'default'
                      }
                    >
                      {type.split('_').join(' ')}
                    </Label>
                  </MenuItem>
                )
              )}
            </RHFSelect>

            <Controller
              name="date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label={watchStatus === ChequeStatus.BANKING ? 'Banking Date' : 'Date'}
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            {watchStatus === ChequeStatus.PAID && (
              <RHFTextField required name="holder" label="Holder" />
            )}

            {/* Same people list as "Received Via" on the create-cheque form */}
            {watchStatus === ChequeStatus.HANDOVER && (
              <RHFCreatableAutocomplete
                required
                name="holder"
                label="Holder"
                options={receivedViaOptions}
                onCreateOption={(value) =>
                  dispatch(createSuggestion({ type: 'receivedVia', value }))
                }
              />
            )}

            {watchStatus === ChequeStatus.RECOVERED &&
              (currentCheque.status === ChequeStatus.HOLDING ||
                currentCheque.status === ChequeStatus.HANDOVER ||
                currentCheque.status === ChequeStatus.RECOVERING) && (
                <Stack spacing={2}>
                  {/* Payment Progress Section */}
                  <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Payment Progress
                    </Typography>

                    {loadingPayments ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Total Amount:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {fCurrencyRupees(currentCheque.amountInCents)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Total Paid:
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {fCurrencyRupees(paymentTotal * 100)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Remaining:
                          </Typography>
                          <Typography variant="body2" color="warning.main" fontWeight="bold">
                            {fCurrencyRupees(currentCheque.amountInCents - paymentTotal * 100)}
                          </Typography>
                        </Stack>

                        <LinearProgress
                          variant="determinate"
                          value={(paymentTotal * 100) / currentCheque.amountInCents / 100}
                          sx={{ height: 8, borderRadius: 1, mt: 1 }}
                        />
                      </Stack>
                    )}
                  </Box>

                  {/* Payment Amount Input */}
                  <RHFTextField
                    name="paymentAmount"
                    label="Payment Amount (Cents)"
                    type="number"
                    required
                    helperText={`Default is remaining amount: ${fCurrencyRupees(
                      currentCheque.amountInCents - paymentTotal
                    )}`}
                  />

                  {/* Payment Note */}
                  <RHFTextField
                    name="paymentNote"
                    label="Payment Note (Optional)"
                    multiline
                    rows={2}
                    placeholder="e.g., Bank transfer, cash payment, etc."
                  />

                  {/* A held cheque never reached a supplier, so there is none to pay. */}
                  {!currentCheque.heldAt && !currentCheque.bankedAt && (
                    <DirectToSupplierCheckbox
                      chequeId={currentCheque._id}
                      amountField="paymentAmount"
                    />
                  )}

                  {/* Payment History */}
                  {paymentHistory.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Previous Payments ({paymentHistory.length})
                      </Typography>

                      <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                        <Stack spacing={1}>
                          {paymentHistory.map((payment) => (
                            <Card
                              key={payment._id}
                              variant="outlined"
                              sx={{ p: 1.5, bgcolor: 'background.paper' }}
                            >
                              <Stack spacing={0.5}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography variant="body2" fontWeight="bold">
                                    {fCurrencyRupees(payment.amountInCents * 100)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {fDate(payment.paymentDate)}
                                  </Typography>
                                </Stack>

                                {payment.note && (
                                  <Typography variant="caption" color="text.secondary">
                                    {payment.note}
                                  </Typography>
                                )}

                                <Typography variant="caption" color="text.disabled">
                                  by {payment.createdBy.firstName} {payment.createdBy.lastName}
                                </Typography>
                              </Stack>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                  )}
                </Stack>
              )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Update
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default UpdateChequeStatus;
